import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import {
  ChevronLeftIcon,
  FamilyIcon,
  UserCanceled2Icon,
  UserCanceledIcon,
  UserCheckIcon
} from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { activateHostFamilies } from '../../api/host-families/activate-families'
import { deactivateHostFamilies } from '../../api/host-families/deactivate-families'
import { getHostFamily } from '../../api/host-families/get-host-family'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import { StatusToggle } from '../../components/elements/status-toggle'
import { HostFamilyDeactivationForm } from '../../components/pages/ols/host-family/host-family-deactivation-form'
import HostFamilyInformationData from '../../components/pages/ols/host-family/host-family-information/host-family-information-data'
import HostFamilyLecCommentTable from '../../components/pages/ols/host-family/host-family-information/host-family-lec-comment-table'
import HostFamilyOlComments from '../../components/pages/ols/host-family/host-family-information/host-family-ol-comments'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const OlHostFamilySinglePage = () => {
  const { back } = useNavigator()
  const id = useParams<{ id: string }>().id
  const { modalState, toggleModal, closeModel } = useModalManager(['deactivate'])
  const [deactivated, setDeactivated] = useState(false)
  const queryClient = useQueryClient()

  const { data: hostFamily, isPending } = useQuery({
    queryKey: ['host-family', id],
    queryFn: () => {
      if (!id || isNaN(+id)) return
      return getHostFamily(+id)
    },
    enabled: !!id
  })

  useEffect(() => {
    if (hostFamily?.deactivated) {
      setDeactivated(true)
    }
  }, [hostFamily])

  const { mutateAsync: activateHostFamiliesMutation } = useMutation({
    mutationFn: activateHostFamilies,
    onError: (error) => {
      showAxiosError(error)
      setDeactivated(true)
    },
    onSuccess: () => {
      toast.success("Famille d'accueil activée")
      setDeactivated(false)
      queryClient.invalidateQueries({
        queryKey: ['host-family']
      })
      closeModel('deactivate')
    }
  })

  const { mutateAsync: deactivateHostFamiliesMutation } = useMutation({
    mutationFn: deactivateHostFamilies,
    onError: (error) => {
      showAxiosError(error)
      setDeactivated(false)
    },
    onSuccess: () => {
      toast.success("Famille d'accueil désactivée")
      setDeactivated(true)
      queryClient.invalidateQueries({
        queryKey: ['host-family']
      })
      closeModel('deactivate')
    }
  })

  if (!id || isNaN(+id)) return
  if (isPending) return <LoadingScreen />
  if (!hostFamily) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={hostFamily.familyName} icon={FamilyIcon} />
      <div className="h-4" />
      <Box className="flex flex-col gap-4 ">
        <Button
          icon={ChevronLeftIcon}
          label="Retour"
          variant="ghost"
          className="border-grey-light w-fit"
          onClick={() => back()}
        />
        <div className="flex justify-between items-center w-full">
          <div className="flex gap-2 items-center">
            <Typography variant="h6" className="mr-2">
              FAMILY {hostFamily?.familyName.toUpperCase()}
            </Typography>
            <Badge
              variant={hostFamily.deactivated ? 'danger' : 'success'}
              text={hostFamily.deactivated ? 'Inactif' : 'Actif'}
              iconPosition="start"
              size="medium"
              icon={
                hostFamily.deactivated ? (
                  <UserCanceledIcon color={tailwindColors.red.DEFAULT} />
                ) : (
                  <UserCheckIcon color={tailwindColors.green.DEFAULT} />
                )
              }
            />
            {
              //TODO: Uncomment this when the Badge component is ready
              /* <Badge
              variant="grey"
              text="Non Disponible en AOÛT"
              textColor={tailwindColors.secondary.DEFAULT}
              iconPosition="start"
              size="medium"
              icon={<NoteIcon />}
            /> */
            }
          </div>
          <div className="flex items-center gap-4">
            <StatusToggle
              isActive={!deactivated}
              onToggle={(value) => {
                if (value) {
                  activateHostFamiliesMutation([+id])
                } else {
                  setDeactivated(true)
                  toggleModal('deactivate')
                }
              }}
            />
          </div>
        </div>
        <HostFamilyInformationData data={hostFamily} view={'complex'} />
      </Box>
      <div className={'h-4'} />
      <Box>
        <Box className="bg-white-hover">
          <Typography variant="text">Commentaires</Typography>
          <div className={'h-4'} />

          <HostFamilyOlComments id={Number(id)} />
          <div className={'h-4'} />
          <HostFamilyLecCommentTable id={Number(id)} />
        </Box>
      </Box>
      <Modal
        isOpen={modalState.deactivate}
        title={t('host.family.forms.deactivation.label')}
        icon={UserCanceled2Icon}
        hideActions
        onCancel={() => {
          setDeactivated(false)
          closeModel('deactivate')
        }}
      >
        <HostFamilyDeactivationForm
          defaultValues={{ ids: [+id] }}
          buttonsConfig={modalButtonConfig({
            confirmText: t('host.family.forms.deactivation.submit.label'),
            cancelText: t('host.family.forms.deactivation.cancel.label'),
            onCancel: () => {
              setDeactivated(false)
              closeModel('deactivate')
            },
            onConfirm: deactivateHostFamiliesMutation
          })}
        />
      </Modal>
    </div>
  )
}

export default OlHostFamilySinglePage
